/**
 * THE VIDEO EDITOR (v1.0.29, `/videos/$articleId/edit`).
 *
 * A video is a kind of article: the same working copy, autosave, conflict pane, statuses, review
 * notes and Send as the article editor (../articles/EditorRoute.tsx), whose parts this screen
 * reuses rather than copies. What differs is what the piece is made of:
 *   - THE VIDEO (./VideoBlock.tsx) in place of pictures - an uploaded file the server converts,
 *     or a YouTube link - and its cover (./VideoCover.tsx), a frame, YouTube's still or the
 *     author's own picture, previewed in the video's shape;
 *   - A TEXT-ONLY DESCRIPTION: paragraphs, quotes and dividers, no "Add an image" (the autosave
 *     refuses image blocks on a video);
 *   - THE EIGHT RULES of the video guidelines (./VideoRights.tsx), kept on the draft as they are
 *     ticked and all ticked before Send, which sends them (`videoConfirmations`); Send is held,
 *     focusable and saying why, until they are;
 *   - THE CHECKLIST over `getStructuralRules?kind=video` - no word floor, no picture rule - plus
 *     "the video is ready" and "all eight confirmed" (./video-checklist.ts).
 * Every video is reviewed by a person, so the screen never speaks of automatic checks.
 *
 * ★ A LIVE STREAM (1.2.0, `WorkingCopy.stream` set) is a video born as a stream: a YouTube link
 * only (no upload), its planned start and end (./StreamTimes.tsx) in place of the eight rules, a
 * checklist row for those times in place of theirs, and a Send that sends no confirmations and
 * waits for the times to be saved with the start still ahead.
 *
 * A piece the author cannot change (in review, published) is shown, not edited
 * (./VideoReadView.tsx), as the article editor does.
 */
import {
  Alert,
  Button,
  Divider,
  Stack,
  Tabs,
  Text,
  TextInput,
  Textarea,
  Title,
  VisuallyHidden,
} from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from '@tanstack/react-router';
import { ListChecks, MessageCircle, Send, SlidersHorizontal } from '@moonx/ui';
import type { LucideIcon } from '@moonx/ui';
import {
  EditorApiProvider,
  VIDEO_RULES,
  isStructuralLimitsRefusal,
  isTermsAcceptanceStale,
  useEditorApi,
} from '../../features/cabinet/api/editor-api';
import type {
  EditorApi,
  ReviewFinding,
  StructuralLimitFailure,
  VideoRule,
  WorkingCopy,
  WorkingStream,
} from '../../features/cabinet/api/editor-api';
import { createEditorApi } from '../../features/cabinet/api/editor-api-source';
import { articleWordCount } from '../../features/cabinet/api/editor-measure';
import {
  APPLICANT_ERROR_CODE,
  NotFoundFailure,
  hasErrorCode,
  isApiProblem,
} from '../../features/cabinet/api/problem';
import { describeProblem } from '../../features/cabinet/api/problem';
import { ErrorState, LoadingState, StatusPill } from '../../features/cabinet/components';
import { useAction, useAsync } from '../../features/cabinet/state';
import { useOptionalAuthSession } from '../../features/auth/session/AuthSessionProvider';
import { writesDraftsBeforeApproval } from '../../features/auth/session/session-state';
import { InAppAnchor } from '../../features/shell/InAppAnchor';
import { createQuarantineUploader } from '../../features/storage/create-quarantine-uploader';
import type { QuarantineUploader } from '../../features/application/api/types';
import { Trans, useTranslation } from '../../i18n';
import { APPLY_ROUTE } from '../apply';
import { usePlatformMonth } from '../calendar/platform-month';
import {
  NO_TAG_GROUPS,
  STANDFIRST_FIELD,
  SaveStateLine,
  TITLE_FIELD,
  editorHrefFor,
  recordMoved,
  resolveRefusalMessage,
  submitRefusalMessage,
  useTagSearch,
  wordsLabel,
} from '../articles/EditorRoute';
import { BlockList } from '../articles/editor/BlockList';
import { ConflictPanel } from '../articles/editor/ConflictPanel';
import { MissingBeforeSending } from '../articles/editor/MissingBeforeSending';
import { ReviewNotes } from '../articles/editor/ReviewNotes';
import { SetupPanel } from '../articles/editor/SetupPanel';
import { ApprovalFirst, SubmitChecklist, SubmitRefusal } from '../articles/editor/SubmitChecklist';
import { TermsStaleNotice } from '../articles/editor/TermsStaleNotice';
import { addressCategoryRow } from '../articles/editor/address';
import {
  UploadsInFlightProvider,
  createUploadsInFlight,
  useUploadsInFlight,
  useUploadsInFlightCount,
} from '../articles/editor/uploads-in-flight';
import { EditorUploaderProvider } from '../articles/editor/use-article-image-upload';
import { useEditorDraft } from '../articles/editor/use-editor-draft';
import { hasUnsavedWork, useLeaveWarning } from '../articles/editor/use-leave-warning';
import { PieceContextProvider } from '../articles/editor/piece-context';
import editorClasses from '../articles/editor/editor.module.css';
import type { ArticlesKey } from '../articles/articles-i18n';
import { VideoBlock } from './VideoBlock';
import { VideoCover } from './VideoCover';
import { VideoReadView } from './VideoReadView';
import { VideoRights } from './VideoRights';
import { StreamTimesSection, useStreamTimes } from './StreamTimes';
import { VIDEOS_HREF, videoEditorHrefFor } from './paths';
import { VIDEO_POLL_MS, useArticleVideo } from './use-article-video';
import { VideoMetadataReaderProvider, readVideoMetadata } from './video-metadata';
import type { VideoMetadataReader } from './video-metadata';
import { rightsRow, streamTimesRow, videoRow, videoStructuralRows } from './video-checklist';
import { videoSubmitRefusal } from './video-words';
import classes from './video.module.css';

type VideoTab = 'setup' | 'checklist' | 'notes';

const VIDEO_TABS: readonly { value: VideoTab; label: ArticlesKey; icon: LucideIcon }[] = [
  { value: 'setup', label: 'editor.tabs.setup', icon: SlidersHorizontal },
  { value: 'checklist', label: 'editor.tabs.checklist', icon: ListChecks },
  { value: 'notes', label: 'common.editorNotes', icon: MessageCircle },
];

const SIDE_PANEL_ID = 'video-editor-side';

/** The line beside Send saying why it is held; the button names it as its description. */
const SEND_HELD_ID = 'video-send-held';

function isVideoTab(value: string | null): value is VideoTab {
  return VIDEO_TABS.some((tab) => tab.value === value);
}

export interface VideoEditorScreenProps {
  readonly articleId: string;
  /** Injected for tests; built here when absent, as the article editor's is. */
  readonly api?: EditorApi;
  /** The writer a video's and a picture's bytes go through. Injected for tests. */
  readonly uploader?: QuarantineUploader;
  /** How often a video the server is still preparing is asked about. A spec shortens it. */
  readonly pollMs?: number;
  /** How long a tick that was not kept waits before it is sent again, doubled each time. */
  readonly tickRetryMs?: number;
  /** Reads a chosen file's length and shape before it is uploaded. A spec stands in for it. */
  readonly readMetadata?: VideoMetadataReader;
}

export function VideoEditorScreen({
  articleId,
  api,
  uploader,
  pollMs = VIDEO_POLL_MS,
  tickRetryMs = TICK_RETRY_MS,
  readMetadata = readVideoMetadata,
}: VideoEditorScreenProps) {
  // One instance each for the life of the screen - see ArticleEditorScreen for why.
  const value = useMemo(() => api ?? createEditorApi(), [api]);
  const writer = useMemo(() => uploader ?? createQuarantineUploader(), [uploader]);
  const [uploads] = useState(createUploadsInFlight);
  return (
    <EditorApiProvider value={value}>
      <EditorUploaderProvider value={writer}>
        <UploadsInFlightProvider value={uploads}>
          {/* The article editor's panes, saying "video" where they would say "article". */}
          <PieceContextProvider value="video">
            <VideoMetadataReaderProvider value={readMetadata}>
              <VideoEditorBody articleId={articleId} pollMs={pollMs} tickRetryMs={tickRetryMs} />
            </VideoMetadataReaderProvider>
          </PieceContextProvider>
        </UploadsInFlightProvider>
      </EditorUploaderProvider>
    </EditorApiProvider>
  );
}

function VideoEditorBody({
  articleId,
  pollMs,
  tickRetryMs,
}: {
  articleId: string;
  pollMs: number;
  tickRetryMs: number;
}) {
  const { t } = useTranslation('video');
  const api = useEditorApi();
  const copy = useAsync<WorkingCopy>(
    useCallback((signal) => api.getOwnArticle(articleId, signal), [api, articleId]),
    `video-editor:${articleId}`,
  );
  if (copy.status === 'loading' && copy.data === undefined) {
    return <LoadingState label={t('editor.opening')} rows={6} />;
  }
  if (copy.data === undefined) {
    return <ErrorState title={t('editor.didNotOpen')} error={copy.error} onRetry={copy.refetch} />;
  }
  if (copy.data.kind !== 'video') {
    // An article's address with this screen's path: send the author to the article editor.
    return (
      <Stack gap="sm">
        <Title order={1} size="h3">
          {t('editor.notAVideo')}
        </Title>
        <Text size="sm">
          <InAppAnchor href={editorHrefFor(articleId)}>{t('editor.openAsArticle')}</InAppAnchor>
        </Text>
      </Stack>
    );
  }
  // Keyed on the article, as the article editor's is: the draft hook seeds once.
  return copy.data.stream === null || copy.data.stream === undefined ? (
    <LoadedVideoEditor
      key={articleId}
      initial={copy.data}
      pollMs={pollMs}
      tickRetryMs={tickRetryMs}
    />
  ) : (
    <LoadedStreamEditor
      key={articleId}
      initial={copy.data}
      pollMs={pollMs}
      tickRetryMs={tickRetryMs}
    />
  );
}

/**
 * How often a stream's editor moves the platform's instant on, so "the start is still ahead" is
 * judged now rather than when the page opened. Counted by a timer, which is not a clock read (C11):
 * a page that slept counts short, and the submit still judges.
 */
const STREAM_NOW_STEP_MS = 30_000;

/**
 * A live stream's editor: the video editor, told the platform's own instant - which its times'
 * first day and "the start is still ahead" are judged by (../calendar/platform-month.ts says why it
 * is that read and not the browser's clock) - moved on by the time the page has been open. Only a
 * stream makes the read.
 */
function LoadedStreamEditor(props: { initial: WorkingCopy; pollMs: number; tickRetryMs: number }) {
  const opened = usePlatformMonth(undefined, 'ready').instant;
  const [steps, setSteps] = useState(0);
  useEffect(() => {
    if (opened === undefined) {
      return undefined;
    }
    const timer = setInterval(() => {
      setSteps((count) => count + 1);
    }, STREAM_NOW_STEP_MS);
    return () => {
      clearInterval(timer);
    };
  }, [opened]);
  const platformNow =
    opened === undefined
      ? undefined
      : new Date(Date.parse(opened) + steps * STREAM_NOW_STEP_MS).toISOString();
  return <LoadedVideoEditor {...props} platformNow={platformNow} />;
}

/** A video that is not a stream holds no times; the hook is called all the same. */
const NO_TIMES: WorkingStream = { startsAt: null, endsAt: null };

function LoadedVideoEditor({
  initial,
  pollMs,
  tickRetryMs,
  platformNow,
}: {
  initial: WorkingCopy;
  pollMs: number;
  tickRetryMs: number;
  /** A live stream's: the platform's instant, once read (`LoadedStreamEditor`). */
  platformNow?: string | undefined;
}) {
  const { t } = useTranslation('video');
  const { t: ta, i18n } = useTranslation('articles');
  const api = useEditorApi();
  const state = useEditorDraft(initial);
  const { draft, base } = state;
  const readOnly = base.writableBy !== 'author';
  const uploads = useUploadsInFlight();
  const goingIn = useUploadsInFlightCount(uploads);
  const authSession = useOptionalAuthSession();
  const awaitingApproval = authSession !== null && writesDraftsBeforeApproval(authSession.state);
  const video = useArticleVideo(base.articleId, initial.video ?? null, pollMs);
  const [confirmed, setConfirmed] = useState<ReadonlySet<VideoRule>>(
    () => new Set(initial.videoConfirmations ?? []),
  );
  const allConfirmed = VIDEO_RULES.every((rule) => confirmed.has(rule));
  const keepTicks = useKeptTicks(base.articleId, tickRetryMs);
  // A stream from its first moment, for good (`createVideoDraft`).
  const isStream = initial.stream !== null && initial.stream !== undefined;
  const times = useStreamTimes(base.articleId, initial.stream ?? NO_TIMES);
  const timesRow = useMemo(
    () => (isStream ? streamTimesRow(times.saved, platformNow, t) : null),
    [isStream, times.saved, platformNow, t],
  );
  // What Send waits for beyond an upload: the eight rules - or a stream's times, saved and ahead.
  const sendable = timesRow === null ? allConfirmed : timesRow.state === 'ok' && !times.unsaved;
  // Leaving stops an upload and loses ticks or times not kept yet, so the page asks first while
  // any is so, as it does for unsaved words.
  useLeaveWarning(
    hasUnsavedWork(state.saveState) ||
      goingIn > 0 ||
      ((keepTicks.unsaved || (isStream && times.unsaved)) && !readOnly),
  );

  /* THE TICKS FOLLOW THE COPY THE EDITOR TAKES. The saved version a conflict offers, and the copy
     read again after Send, each carry the ticks the server holds; the page shows those. A copy
     that carries none says nothing about them, and the page keeps its own. */
  const { reseed } = keepTicks;
  const takeTicksOf = useCallback(
    (copy: WorkingCopy) => {
      if (copy.videoConfirmations === undefined) {
        return;
      }
      const rules = new Set(copy.videoConfirmations);
      setConfirmed(rules);
      reseed(rules);
    },
    [reseed],
  );
  const { conflict, takeServerCopy: takeServerDraft } = state;
  const takeServerCopy = useCallback(() => {
    if (conflict !== undefined) {
      takeTicksOf(conflict);
    }
    takeServerDraft();
  }, [conflict, takeServerDraft, takeTicksOf]);

  const [resolving, setResolving] = useState<number | undefined>(undefined);
  const [resolveRefusal, setResolveRefusal] = useState<{ readonly error: unknown } | undefined>(
    undefined,
  );

  const rules = useAsync(
    useCallback((signal) => api.getStructuralRules(signal, { kind: 'video' }), [api]),
    'rules:video',
  );
  const categories = useAsync(
    useCallback((signal) => api.listEditorCategories(signal), [api]),
    `categories:${i18n.language}`,
  );
  const tags = useAsync(
    useCallback((signal) => api.suggestTags({}, signal), [api]),
    'tags',
  );
  const tagSearch = useTagSearch(api);
  const dates = useAsync(
    useCallback((signal) => api.getPublishDateSuggestions({}, signal), [api]),
    'publish-dates',
  );
  const review = useAsync(
    useCallback((signal) => api.getArticleReview(base.articleId, signal), [api, base.articleId]),
    `review:${base.articleId}`,
  );
  const versions = useAsync(
    useCallback((signal) => api.listArticleVersions(base.articleId, signal), [api, base.articleId]),
    `versions:${base.articleId}`,
  );

  const categoryItems = categories.data?.items;
  const checklist = useMemo(
    () =>
      rules.data === undefined
        ? []
        : [
            videoRow(video.video, t),
            ...videoStructuralRows(
              draft,
              base,
              rules.data,
              video.video?.orientation === 'vertical',
              ta,
            ),
            ...(draft.categories.length === 0
              ? []
              : [
                  addressCategoryRow(
                    draft,
                    (code) =>
                      categoryItems?.find((category) => category.code === code)?.label ?? code,
                    ta,
                  ),
                ]),
            timesRow ?? rightsRow(confirmed, t),
          ],
    [rules.data, video.video, draft, base, categoryItems, confirmed, timesRow, t, ta],
  );
  const quoteRule = rules.data?.rules.find((rule) => rule.rule === 'quote_length');

  const findings = review.data?.findings ?? [];
  const hasNotes = findings.length > 0;
  const openNotes = findings.filter((finding) => finding.disposition === 'open').length;
  const [chosenTab, setChosenTab] = useState<VideoTab>(() =>
    initial.status === 'changes_requested' ? 'notes' : 'setup',
  );
  const tab: VideoTab = chosenTab === 'notes' && !hasNotes ? 'setup' : chosenTab;
  const openChecklist = useCallback(() => {
    setChosenTab('checklist');
    document.getElementById(`${SIDE_PANEL_ID}-tab-checklist`)?.focus();
  }, []);

  const submit = useAction(async () => {
    // Save first, as the article editor does: the transition seals what the autosave wrote.
    const saved = await state.saveNow();
    if (!saved || uploads.count() > 0) {
      return undefined;
    }
    // A live stream confirms no rules, and sends no body, as an article does.
    const body = isStream
      ? undefined
      : { videoConfirmations: VIDEO_RULES.filter((rule) => confirmed.has(rule)) };
    return base.status === 'changes_requested'
      ? await api.resubmitArticle(base.articleId, body)
      : await api.submitArticle(base.articleId, body);
  });

  // Held, not disabled: the button stays focusable, and the line beside it says why.
  const sendHeld = !readOnly && !submit.isRunning && (goingIn > 0 || !sendable);
  const heldReason =
    goingIn > 0 ? t('editor.heldUpload') : isStream ? t('editor.heldTimes') : t('editor.heldRules');

  const { take: takeTimes } = times;
  const onSubmit = useCallback(() => {
    if (uploads.count() > 0 || !sendable || submit.isRunning) {
      return;
    }
    void (async () => {
      const moved = await submit.run();
      if (moved !== undefined) {
        // The status moved, and with it who may write: read the copy again rather than patch it.
        const fresh = await api.getOwnArticle(base.articleId);
        state.adopt(fresh);
        takeTicksOf(fresh);
        takeTimes(fresh.stream);
        review.refetch();
        versions.refetch();
      }
    })();
  }, [
    uploads,
    sendable,
    submit,
    api,
    base.articleId,
    state,
    review,
    versions,
    takeTicksOf,
    takeTimes,
  ]);

  const submitRefusal: readonly StructuralLimitFailure[] | undefined = isStructuralLimitsRefusal(
    submit.error,
  )
    ? submit.error.failures
    : undefined;

  const onResolve = useCallback(
    (finding: ReviewFinding) => {
      setResolving(finding.ordinal);
      setResolveRefusal(undefined);
      void api
        .resolveReviewFinding(base.articleId, finding.reviewSeq, finding.ordinal)
        .then(() => {
          review.refetch();
        })
        .catch((error: unknown) => {
          setResolveRefusal({ error });
          if (recordMoved(error)) {
            review.refetch();
          }
        })
        .finally(() => {
          setResolving(undefined);
        });
    },
    [api, base.articleId, review],
  );

  // Each tick is kept at once, the whole set (0132): leaving the page no longer clears them.
  const toggleRule = useCallback(
    (rule: VideoRule, ticked: boolean) => {
      const next = new Set(confirmed);
      if (ticked) {
        next.add(rule);
      } else {
        next.delete(rule);
      }
      setConfirmed(next);
      keepTicks.keep(next);
    },
    [confirmed, keepTicks],
  );

  if (readOnly) {
    return (
      <VideoReadView
        base={base}
        draft={draft}
        video={video}
        stream={isStream ? times.saved : null}
        categories={categories.data?.items ?? []}
        review={review.data}
      />
    );
  }

  const words = articleWordCount(draft);
  const submitLabel =
    base.status === 'changes_requested' ? t('editor.resubmit') : t('editor.submit');

  return (
    <Stack gap="lg">
      <div>
        <div className={editorClasses.top}>
          <div className={editorClasses.crumbs}>
            <InAppAnchor href={VIDEOS_HREF} className={editorClasses.crumbLink} underline="hover">
              {t('editor.crumb')}
            </InAppAnchor>
            <span aria-hidden="true">›</span>
            <h1 className={editorClasses.crumbTitle}>
              {draft.title.trim().length === 0 ? t('list.untitled') : draft.title}
            </h1>
          </div>
          <div className={editorClasses.actions}>
            <StatusPill status={base.status} />
            <SaveStateLine saveState={state.saveState} updatedAt={base.updatedAt} />
            {awaitingApproval ? (
              <ApprovalFirst />
            ) : (
              <Button
                radius="xl"
                leftSection={<Send size={15} aria-hidden />}
                loading={submit.isRunning}
                data-disabled={sendHeld}
                aria-disabled={sendHeld || undefined}
                aria-describedby={sendHeld ? SEND_HELD_ID : undefined}
                onClick={onSubmit}
              >
                {submitLabel}
              </Button>
            )}
          </div>
        </div>
        {/* Always mounted, even empty: a live region that appears with its first message may never
            be announced. */}
        <Text
          id={SEND_HELD_ID}
          role="status"
          size="xs"
          c="dimmed"
          className={editorClasses.heldReason}
        >
          {sendHeld && !awaitingApproval ? heldReason : ''}
        </Text>
      </div>

      <MissingBeforeSending rows={checklist} onOpenChecklist={openChecklist} />

      {state.conflict === undefined ? null : (
        <ConflictPanel
          mine={draft}
          theirs={state.conflict}
          onTakeTheirs={takeServerCopy}
          onKeepMine={state.keepMyCopy}
        />
      )}

      {state.conflict === undefined && state.saveState === 'failed' ? (
        <DangerAlert title={ta('common.changeNotSaved')}>
          <Text size="sm">{describeProblem(state.error, 'video')}</Text>
        </DangerAlert>
      ) : null}

      {isTermsAcceptanceStale(submit.error) ? (
        <TermsStaleNotice
          returnTo={videoEditorHrefFor(base.articleId)}
          onClose={submit.clearError}
        />
      ) : null}

      {submit.error === undefined ||
      isStructuralLimitsRefusal(submit.error) ||
      isTermsAcceptanceStale(submit.error) ? null : (
        <DangerAlert title={ta('common.didNotGoThrough')} onClose={submit.clearError}>
          <Text size="sm">
            {videoSubmitRefusal(submit.error, t) ?? submitRefusalMessage(submit.error, ta, 'video')}
          </Text>
          {hasErrorCode(submit.error, APPLICANT_ERROR_CODE.authorApprovalRequired) ? (
            <Text size="sm">
              <InAppAnchor href={APPLY_ROUTE.path} size="sm">
                {ta('editor.approval.link')}
              </InAppAnchor>
            </Text>
          ) : null}
        </DangerAlert>
      )}

      {submitRefusal === undefined ? null : (
        <SubmitRefusal failures={submitRefusal} rows={checklist} />
      )}

      <div className={editorClasses.layout}>
        <div className={editorClasses.grid}>
          <div className={editorClasses.canvas}>
            <VideoBlock
              articleId={base.articleId}
              state={video}
              disabled={readOnly}
              stream={isStream}
            />

            {isStream ? <StreamTimesSection state={times} platformNow={platformNow} /> : null}

            <VideoCover
              articleId={base.articleId}
              video={video.video}
              coverUrl={base.coverUrl}
              disabled={readOnly}
              onCover={state.setCover}
            />

            <section aria-label={t('editor.textLabel')}>
              <TextInput
                label={ta('editor.titleField')}
                placeholder={isStream ? t('stream.titlePlaceholder') : t('editor.titlePlaceholder')}
                variant="unstyled"
                classNames={{ label: editorClasses.srOnly, input: editorClasses.bare }}
                styles={{ input: TITLE_FIELD }}
                value={draft.title}
                onChange={(event) => {
                  const title = event.currentTarget.value;
                  state.edit((current) => ({ ...current, title }));
                }}
              />
              <Textarea
                label={ta('editor.subtitleField')}
                description={t('editor.subtitleNote')}
                placeholder={ta('editor.subtitleField')}
                autosize
                minRows={1}
                variant="unstyled"
                mt={6}
                classNames={{
                  label: editorClasses.srOnly,
                  description: editorClasses.srOnly,
                  input: editorClasses.bare,
                }}
                styles={{ input: STANDFIRST_FIELD }}
                value={draft.subtitle}
                onChange={(event) => {
                  const subtitle = event.currentTarget.value;
                  state.edit((current) => ({ ...current, subtitle }));
                }}
              />
              <div className={editorClasses.meta}>
                <span>{wordsLabel(words, ta)}</span>
              </div>
            </section>

            <section className={classes.section} aria-labelledby="video-description-title">
              <Stack gap={2}>
                <Title id="video-description-title" order={2} size="h5">
                  {t('editor.description')}
                </Title>
                <Text className={classes.hint}>{t('editor.descriptionNote')}</Text>
              </Stack>
              <BlockList
                articleId={base.articleId}
                blocks={draft.blocks}
                disabled={readOnly}
                serverInstant={base.updatedAt}
                onChange={state.edit}
                quoteBodyMax={quoteRule?.bodyMax ?? quoteRule?.max ?? null}
                quoteAttributionMax={quoteRule?.attributionMax ?? null}
                allowImages={false}
              />
            </section>

            {isStream ? null : (
              <>
                <Divider my="lg" />
                <VideoRights
                  confirmed={confirmed}
                  disabled={readOnly}
                  notKept={
                    keepTicks.failure === undefined
                      ? undefined
                      : keepTicks.failure.retryable
                        ? t('rights.notKept')
                        : describeProblem(keepTicks.failure.error, 'video')
                  }
                  onToggle={toggleRule}
                />
              </>
            )}
          </div>

          <div className={editorClasses.panel}>
            <Tabs
              id={SIDE_PANEL_ID}
              value={tab}
              onChange={(value) => {
                if (isVideoTab(value)) {
                  setChosenTab(value);
                }
              }}
              keepMounted
              keepMountedMode="display-none"
              unstyled
              classNames={{
                list: editorClasses.tabList,
                tab: editorClasses.tab,
                tabSection: editorClasses.tabSection,
              }}
            >
              <Tabs.List aria-label={t('editor.panelsLabel')}>
                {VIDEO_TABS.filter((entry) => entry.value !== 'notes' || hasNotes).map(
                  ({ value, label, icon: Icon }) => (
                    <Tabs.Tab
                      key={value}
                      value={value}
                      leftSection={<Icon size={13} aria-hidden />}
                      rightSection={
                        value === 'notes' && openNotes > 0 ? (
                          <span className={editorClasses.tabCount} aria-hidden="true">
                            {openNotes}
                          </span>
                        ) : undefined
                      }
                    >
                      {ta(label)}
                      {value === 'notes' && openNotes > 0 ? (
                        <VisuallyHidden component="span">
                          {`, ${ta('editor.tabs.open', { count: openNotes })}`}
                        </VisuallyHidden>
                      ) : null}
                    </Tabs.Tab>
                  ),
                )}
              </Tabs.List>

              <Tabs.Panel value="setup">
                <SetupPanel
                  draft={draft}
                  disabled={readOnly}
                  categories={categories.data?.items ?? []}
                  tagSuggestions={(tagSearch.found ?? tags.data?.items ?? []).map(
                    (item) => item.tagText,
                  )}
                  tagGroups={tags.data?.groups ?? NO_TAG_GROUPS}
                  onTagSearch={tagSearch.onTagSearch}
                  publishDates={dates.data?.items ?? []}
                  onChange={state.edit}
                  languageFixed={base.status !== 'draft' || (versions.data?.items.length ?? 0) > 0}
                  languageRefused={state.languageRefused}
                  title={isStream ? t('stream.setupTitle') : t('editor.setupTitle')}
                  intro={t('editor.setupIntro')}
                />
              </Tabs.Panel>

              <Tabs.Panel value="checklist">
                {rules.data === undefined ? (
                  rules.status === 'error' ? (
                    <ErrorState
                      title={ta('editor.checklist.didNotLoad')}
                      error={rules.error}
                      onRetry={rules.refetch}
                    />
                  ) : (
                    <LoadingState label={ta('editor.checklist.reading')} rows={4} />
                  )
                ) : (
                  <SubmitChecklist
                    rows={checklist}
                    policyVersion={rules.data.policyVersion}
                    wordCountDefinition={rules.data.wordCountDefinition}
                    readOnly={readOnly}
                    refusal={undefined}
                    awaitingApproval={awaitingApproval}
                  />
                )}
              </Tabs.Panel>

              {review.data === undefined || !hasNotes ? null : (
                <Tabs.Panel value="notes">
                  <Stack gap="md">
                    {resolveRefusal === undefined ? null : (
                      <DangerAlert
                        title={ta('editor.resolveFailed')}
                        onClose={() => {
                          setResolveRefusal(undefined);
                        }}
                      >
                        <Text size="sm">
                          {resolveRefusalMessage(resolveRefusal.error, ta, 'video')}
                        </Text>
                      </DangerAlert>
                    )}
                    <ReviewNotes
                      review={review.data}
                      blocks={draft.blocks}
                      onResolve={onResolve}
                      resolvingOrdinal={resolving}
                      disabled={readOnly}
                    />
                  </Stack>
                </Tabs.Panel>
              )}
            </Tabs>
          </div>
        </div>
      </div>
    </Stack>
  );
}

/** How many times one set of ticks is sent before the pane says it was not kept. */
const TICK_TRIES = 3;

/** The first wait before a tick is sent again; each later wait is twice the one before. */
const TICK_RETRY_MS = 1_000;

interface TicksFailure {
  readonly error: unknown;
  /** False for a refusal sending again cannot change - the video is no longer the author's to write. */
  readonly retryable: boolean;
}

/**
 * Whether a refused send may go through if sent again: no network, a 5xx, a 408 or a 429, or
 * anything that is not the platform's answer. Any other 4xx is the platform saying no - the video
 * moved on, or the session ended - and is said instead.
 */
function worthRetrying(error: unknown): boolean {
  if (error instanceof NotFoundFailure) {
    return false;
  }
  if (isApiProblem(error)) {
    return error.status >= 500 || error.status === 429 || error.status === 408;
  }
  return true;
}

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/**
 * KEEPS THE TICKED RULES (`setVideoDraftConfirmations`, 0132). Each tick sends the whole set, one
 * request at a time, and only the latest set waits behind the one in flight - so the last one the
 * server applies is the last tick, and a run of ticks is not a run of requests. A send that fails is
 * sent again, a few times with growing waits, unless a newer set has come meanwhile; once it has
 * failed for good the ticks stay on the page and the pane says so. `unsaved` is true while a set
 * waits, flies or has failed, so the page asks before it is left.
 */
function useKeptTicks(
  articleId: string,
  retryMs: number,
): {
  readonly keep: (rules: ReadonlySet<VideoRule>) => void;
  /** The copy the editor took holds these: nothing of this page's is owed any more. */
  readonly reseed: (rules: ReadonlySet<VideoRule>) => void;
  readonly unsaved: boolean;
  readonly failure: TicksFailure | undefined;
} {
  const api = useEditorApi();
  const [sending, setSending] = useState(false);
  const [failure, setFailure] = useState<TicksFailure | undefined>(undefined);
  /** The newest set not sent yet; null when there is none. */
  const waiting = useRef<readonly VideoRule[] | null>(null);
  const running = useRef(false);
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /** One set, tried up to `TICK_TRIES` times. `superseded` when a newer set is waiting. */
  const send = useCallback(
    async (rules: readonly VideoRule[]): Promise<'kept' | 'superseded' | TicksFailure> => {
      for (let attempt = 1; ; attempt += 1) {
        try {
          await api.setVideoDraftConfirmations(articleId, { rules: [...rules] });
          return 'kept';
        } catch (error) {
          const retryable = worthRetrying(error);
          if (!retryable || attempt >= TICK_TRIES) {
            return { error, retryable };
          }
        }
        await pause(retryMs * 2 ** (attempt - 1));
        if (waiting.current !== null || !alive.current) {
          return 'superseded';
        }
      }
    },
    [api, articleId, retryMs],
  );

  const drain = useCallback(async () => {
    if (running.current) {
      return;
    }
    running.current = true;
    setSending(true);
    try {
      while (waiting.current !== null && alive.current) {
        const rules = waiting.current;
        waiting.current = null;
        const outcome = await send(rules);
        if (outcome === 'kept') {
          setFailure(undefined);
        } else if (outcome !== 'superseded' && waiting.current === null) {
          setFailure(outcome);
        }
      }
    } finally {
      running.current = false;
      if (alive.current) {
        setSending(false);
      }
    }
  }, [send]);

  const keep = useCallback(
    (rules: ReadonlySet<VideoRule>) => {
      waiting.current = VIDEO_RULES.filter((rule) => rules.has(rule));
      void drain();
    },
    [drain],
  );

  const reseed = useCallback((rules: ReadonlySet<VideoRule>) => {
    setFailure(undefined);
    // A set already in flight may land after the copy was read: the server's own set follows it,
    // so the server ends where the page is. With nothing in flight there is nothing to send.
    waiting.current = running.current ? VIDEO_RULES.filter((rule) => rules.has(rule)) : null;
  }, []);

  const unsaved = sending || failure !== undefined;
  return useMemo(() => ({ keep, reseed, unsaved, failure }), [keep, reseed, unsaved, failure]);
}

/** A refusal, in the editor's danger tint (the article editor's alerts, one shape). */
function DangerAlert({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose?: () => void;
  children: ReactNode;
}) {
  const { t } = useTranslation('articles');
  return (
    <Alert
      variant="light"
      role="alert"
      title={title}
      withCloseButton={onClose !== undefined}
      closeButtonLabel={t('common.dismiss')}
      {...(onClose === undefined ? {} : { onClose })}
      styles={{
        root: {
          backgroundColor: 'oklch(var(--mx-danger) / 0.10)',
          border: '1px solid oklch(var(--mx-danger) / 0.45)',
        },
        title: { color: 'oklch(var(--mx-danger))' },
      }}
    >
      {children}
    </Alert>
  );
}

/**
 * The route component: the only part that knows about the router, as `EditorRoute` is for the
 * article editor - the screen itself takes an id and is testable without a router.
 */
export function VideoEditorRoute() {
  const { t } = useTranslation('video');
  const params = useParams({ strict: false }) as Readonly<Record<string, string | undefined>>;
  const articleId = params['articleId'];
  if (articleId === undefined || articleId.length === 0) {
    return (
      <Stack gap="sm">
        <Title order={1} size="h3">
          {t('editor.noVideo.title')}
        </Title>
        <Text size="sm">
          <Trans
            t={t}
            i18nKey="editor.noVideo.text"
            components={{ anchor: <InAppAnchor href={VIDEOS_HREF}>{null}</InAppAnchor> }}
          />
        </Text>
      </Stack>
    );
  }
  return <VideoEditorScreen articleId={articleId} />;
}
